import { Box, Text, Flex } from "@chakra-ui/react";
import { colors } from "../../theme";

/**
 * AppSelect - Reusable select component compatible with React Hook Form
 * 
 * @param {string} label - Label text for the select
 * @param {string} name - Field name for form registration
 * @param {function} register - React Hook Form register function
 * @param {object} errors - React Hook Form errors object
 * @param {boolean} required - Whether field is required
 * @param {array} options - Array of options { value, label }
 * @param {string} placeholder - Placeholder text
 * @param {boolean} disabled - Whether select is disabled
 * @param {object} selectProps - Additional props for select
 * @param {any} value - Controlled value (for Controller usage)
 * @param {function} onChange - Controlled onChange handler (for Controller usage)
 * @param {object} error - Error object (for Controller usage)
 */
const AppSelect = ({
  label,
  name,
  register,
  errors,
  required = false,
  options = [],
  placeholder = "Select an option",
  disabled = false,
  selectProps = {},
  value,
  onChange,
  error,
  ...rest
}) => {
  // Support both register mode (errors[name]) and Controller mode (error prop)
  const hasError = error || (errors && errors[name]);

  // Determine if using controlled mode (Controller) or register mode
  const isControlled = value !== undefined || onChange !== undefined;

  const handleChange = (e) => {
    const selectedValue = e.target.value;
    if (onChange) {
      // Pass the actual value, not the event
      onChange(selectedValue || undefined);
    }
  };

  return (
    <Box w="100%" {...rest}>
      {label && (
        <Flex mb={1.5} alignItems="center">
          <Text
            fontSize="sm"
            fontWeight="500"
            color={colors.text}
          >
            {label}
          </Text>
          {required && (
            <Text color={colors.primary} ml={1}>
              *
            </Text>
          )}
        </Flex>
      )}
      <Box position="relative">
        <select
          disabled={disabled}
          value={isControlled ? (value || "") : undefined}
          onChange={isControlled ? handleChange : undefined}
          {...(!isControlled && register ? register(name, { required: required ? `${label || name} is required` : false }) : {})}
          style={{
            width: "100%",
            padding: "8px 16px",
            fontSize: "14px",
            backgroundColor: disabled ? colors.disabled : "white",
            border: `1px solid ${hasError ? colors.primary : colors.disabled}`,
            borderRadius: "8px",
            color: colors.text,
            cursor: disabled ? "not-allowed" : "pointer",
            appearance: "none",
            outline: "none",
          }}
          {...selectProps}
        >
          <option value="">
            {placeholder}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <Box
          position="absolute"
          right={4}
          top="50%"
          transform="translateY(-50%)"
          pointerEvents="none"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill={colors.textSecondary}
          >
            <path d="M7 10l5 5 5-5z" />
          </svg>
        </Box>
      </Box>
      {hasError && (
        <Text color={colors.primary} fontSize="xs" mt={1}>
          {error?.message || errors?.[name]?.message || `${label || name} is required`}
        </Text>
      )}
    </Box>
  );
};

export default AppSelect;
